import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography, useMediaQuery } from '@mui/material';

import { SelectedBottomStyled } from 'enrollment/features/Benefits/BenefitsPreview/GroupedBenefits/GroupedBenefitsStyled';
import { IncrementDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { SingleIncrementInfoStyled } from './SingleIncrementCardStyled';
import { getStringLookupCode } from './utils';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import {
  PlanDetailsBoxStyled,
  PlanPremiumBoxStyled,
} from 'enrollment/features/Plans/PlanCard/PlanDetailsStyled';
import { selectPerPayPeriodText } from 'enrollment/services';

export interface SingleIncrementCardProps {
  increment: IncrementDto;
  benefitType: string;
  hideEmptyCost: boolean;
}

export const SingleIncrementCard = ({
  increment,
  benefitType,
  hideEmptyCost,
}: SingleIncrementCardProps) => {
  const { t } = useTranslation('pages.volume');

  const perPayText = useSelector(selectPerPayPeriodText);

  const premiumSplit = Number(increment.subscriber_premium)
    .toFixed(2)
    .split('.') || ['0', '00'];

  // this allows for the possibility of different strings for different benefit types
  const stringLookupCode = getStringLookupCode(benefitType);
  const factoids: Array<string> = t(
    `enrollment.benefits:benefitFactiods.${stringLookupCode}`,
    { returnObjects: true }
  );
  const factoidItems: Array<string> = Array.isArray(factoids) ? factoids : [];

  const isDesktop = useMediaQuery('(min-width: 1199px)');
  const isMobile = useMediaQuery('(max-width: 744px)');

  return (
    <>
      <PlanDetailsBoxStyled
        style={{
          height: 'fit-content',
          alignItems: 'center',
          padding: '0px 40px 20px 40px',
        }}
        data-testid="single-inc-vol"
      >
        <PlanPremiumBoxStyled
          style={{
            width: `${isMobile ? '100%' : '400px'}`,
            padding: `${isMobile ? '0px' : '0px 40px'}`,
          }}
        >
          <div style={{ minHeight: '88px' }}>
            {(!hideEmptyCost || Number(increment.subscriber_premium) > 0) && (
              <>
                <div className="primary-content text-center headline-1">
                  ${premiumSplit[0]}.
                  <sup
                    data-testid="sub-premium-cents"
                    style={{ fontSize: '0.6em' }}
                  >
                    {premiumSplit[1] || '00'}
                  </sup>
                </div>
                <p className="body2 mb-0">{`${perPayText}`}</p>
              </>
            )}
          </div>
          <div className="d-flex justify-content-between mt-3 pt-4">
            {increment.volume && (
              <SelectedBottomStyled
                data-testid="volume-display"
                className="text-center"
              >
                <Typography variant="body2" whiteSpace="nowrap">
                  {t('enrollment.benefits:CovAmount')}
                </Typography>
                <div className="m-0 h3">
                  $
                  {t('common:numberWithOptions', {
                    value: increment.volume,
                  })}
                </div>
              </SelectedBottomStyled>
            )}
          </div>
        </PlanPremiumBoxStyled>
        <SingleIncrementInfoStyled>
          <p className="body2 m-3 pt-2">{t('singleIncSubhead')}</p>
          <ul style={{ listStyleType: 'disc' }}>
            {factoidItems?.map((string, index) => (
              <li
                data-testid="info-string-item"
                className="semiBold pb-4"
                key={index}
              >
                {string}
              </li>
            ))}
          </ul>
        </SingleIncrementInfoStyled>
      </PlanDetailsBoxStyled>
      {increment.requires_eoi && (
        <div className="d-flex w-100 justify-content-center">
          <div
            className="pt-2"
            style={{ padding: `${isDesktop ? '20px 80px' : '20px 40px'}` }}
          >
            <PSBannerWrapper
              id="eoi-notice"
              data-testid="eoi-notice"
              message={t('eoiNotice')}
              level="info"
              icon={true}
            />
          </div>
        </div>
      )}
    </>
  );
};
